import { CalendarIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { format } from "date-fns";
import { enUS, es, fr, he, ru } from "date-fns/locale";
import { useCallback, useEffect, useRef, useState } from "react";
import { DayPicker, type Matcher } from "react-day-picker";
import dayPickerStyles from "react-day-picker/style.module.css";
import { useClientTranslations } from "../../../../lib/i18n/client";
import styles from "./DatePicker.module.css";
import { FormField } from "./FormField";

const LOCALE_MAP = { en: enUS, es, fr, he, ru } as const;

interface DatePickerProps {
	id: string;
	label: string;
	required?: boolean;
	error?: string;
	placeholder?: string;
	selected?: Date;
	onSelect: (d: Date | undefined) => void;
	/**
	 * Earliest navigable month (react-day-picker v9 `startMonth`). Replaces the
	 * deprecated `fromDate` v8 prop — does NOT disable past days, only restricts
	 * the visible month range. Use `disabled` to gate selection (WR-03).
	 */
	fromDate?: Date;
	/**
	 * Disable specific dates from selection (e.g. `{ before: new Date() }`).
	 * Forwarded directly to react-day-picker v9 `disabled` matcher.
	 */
	disabled?: Matcher | Matcher[];
}

/**
 * Phase 37 — Date picker built on react-day-picker v9.
 *
 * D-10 + D-12: inline-expanding calendar below the trigger field (not a modal,
 * not a nested sheet). Locale-aware via date-fns; `dir='rtl'` for Hebrew;
 * weekStartsOn lock (=1, Monday) to match Phase 36 calendar (Pitfall 10).
 *
 * Capacitor iOS WKWebView Hebrew: react-day-picker reads month/weekday names
 * from the date-fns locale prop, bypassing native Intl (Pitfall 8). Physical
 * iOS device sign-off required before merge (VALIDATION §Manual-Only row 37-MANUAL-01).
 */
export function DatePicker({
	id,
	label,
	required,
	error,
	placeholder,
	selected,
	onSelect,
	fromDate,
	disabled,
}: DatePickerProps) {
	const { t, locale } = useClientTranslations("track");
	const dateFnsLocale = LOCALE_MAP[locale as keyof typeof LOCALE_MAP] ?? enUS;
	const isRtl = locale === "he";
	const [open, setOpen] = useState(false);
	const rootRef = useRef<HTMLDivElement>(null);

	const close = useCallback(() => setOpen(false), []);

	useEffect(() => {
		if (!open) return;
		const onPointer = (e: PointerEvent) => {
			if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
				close();
			}
		};
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") close();
		};
		document.addEventListener("pointerdown", onPointer);
		document.addEventListener("keydown", onKey);
		return () => {
			document.removeEventListener("pointerdown", onPointer);
			document.removeEventListener("keydown", onKey);
		};
	}, [open, close]);

	const displayValue = selected
		? format(selected, "PP", { locale: dateFnsLocale })
		: (placeholder ?? t("event.add.fields.date.placeholder"));

	return (
		<FormField id={id} label={label} required={required} error={error}>
			<div ref={rootRef} className={styles.root}>
				<button
					id={id}
					type="button"
					className={`${styles.trigger} ${selected ? "" : styles.triggerEmpty}`}
					onClick={() => setOpen((v) => !v)}
					aria-expanded={open}
					aria-haspopup="dialog"
					aria-describedby={error ? `${id}-error` : undefined}
				>
					<CalendarIcon className={styles.triggerIcon} aria-hidden="true" />
					<span className={styles.triggerLabel}>{displayValue}</span>
					<ChevronDownIcon
						className={`${styles.chevron} ${open ? styles.chevronOpen : ""}`}
						aria-hidden="true"
					/>
				</button>
				{open ? (
					<div className={styles.popover} role="dialog" aria-label={label}>
						<DayPicker
							mode="single"
							selected={selected}
							onSelect={(d) => {
								onSelect(d);
								setOpen(false);
							}}
							locale={dateFnsLocale}
							dir={isRtl ? "rtl" : "ltr"}
							weekStartsOn={1}
							startMonth={fromDate}
							disabled={disabled}
							classNames={{
								...dayPickerStyles,
								root: styles.calendar,
								day_selected: styles.daySelected,
								day_today: styles.dayToday,
							}}
						/>
					</div>
				) : null}
			</div>
		</FormField>
	);
}
